import { ReactNode } from 'react';

interface PanelProps {
  title: string;
  /** Right-hand side of the panel header: counts, links, meta. */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * The one container shape on the page.
 *
 * Every status page worth trusting uses a single repeated surface rather than a
 * mix of cards, callouts and boxes — Vercel goes as far as using no card at
 * all. One shell here means the services list and the error feed share a
 * border, radius and spacing rhythm, so the eye reads them as one document.
 */
export function Panel({ title, action, children }: PanelProps) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * Rows separated by hairlines instead of gaps — denser and calmer than a grid
 * of cards, and it gives the status column a continuous edge to align to.
 */
export function PanelRows({ children }: { children: ReactNode }) {
  return <div className="divide-y divide-slate-100">{children}</div>;
}
